import { Fragment } from "react";
import { useRouter } from "@/lib/navigation";
import { ArrowLeft, Menu } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  SidebarTrigger,
  useSidebar,
} from "@myela/ui";
import { Truncate } from "@/components/common/Truncate";
import { useBreadcrumbs, useHeaderActions } from "@/components/layout/BreadcrumbContext";

// Slim content header: sidebar toggle (mobile) + breadcrumb, with page actions
// pinned right. Replaces the old TopBar navbar — the user/theme/notifications
// controls now live in the sidebar footer menu.
export function PartnerHeader() {
  const router = useRouter();
  const { isDesktop } = useSidebar();
  const crumbs = useBreadcrumbs();
  const headerActions = useHeaderActions();
  // One level up: the crumb before the current page, when it's linked.
  const parent = crumbs.length >= 2 ? crumbs[crumbs.length - 2] : null;
  const backCrumb = parent && (parent.onClick || parent.href) ? parent : null;

  // List/index pages publish no breadcrumb — on desktop there's nothing to show,
  // so render no bar at all (content sits flush under the sidebar). Mobile always
  // keeps the bar for the sidebar trigger. Header actions (e.g. Viewing as) also
  // keep the bar visible so they stay aligned with the crumb row.
  const hasContent = crumbs.length > 0 || !!headerActions;
  if (isDesktop && !hasContent) return null;

  return (
    // z-30: above scrolling page content, below overlays (sheets z-50, dialogs z-200).
    // Borderless — blends into the page; horizontal padding matches PageContainer
    // so the breadcrumb lines up with the content below it.
    <header className="sticky top-0 z-30 flex h-12 shrink-0 items-center gap-2 bg-background px-4 text-foreground sm:px-6 lg:px-8">
      {/* Mobile/tablet: the sidebar is a closed Sheet, so it needs a trigger here. */}
      {!isDesktop && (
        <SidebarTrigger
          aria-label="Open navigation"
          icon={<Menu className="size-4" />}
          className="mx-0! -ml-1 shrink-0"
        />
      )}

      {backCrumb && (
        <button
          type="button"
          aria-label={typeof backCrumb.label === "string" ? `Back to ${backCrumb.label}` : "Back"}
          title={typeof backCrumb.label === "string" ? `Back to ${backCrumb.label}` : "Back"}
          onClick={() => {
            if (backCrumb.onClick) backCrumb.onClick();
            else if (backCrumb.href) router.push(backCrumb.href);
          }}
          className="inline-flex shrink-0 cursor-pointer items-center text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
      )}

      {/* min-w-0 + flex-nowrap let the trail shrink instead of wrapping onto a
          second line the h-12 header has no room for; the last crumb absorbs it. */}
      <Breadcrumb className="min-w-0">
        <BreadcrumbList className="flex-nowrap">
          {crumbs.map((crumb, i) => {
            const isLast = i === crumbs.length - 1;
            const linked = !isLast && (crumb.href || crumb.onClick);
            return (
              <Fragment key={i}>
                <BreadcrumbItem className={isLast ? "min-w-0" : "shrink-0"}>
                  {linked ? (
                    <BreadcrumbLink
                      href={crumb.href ?? "#"}
                      onClick={(e) => {
                        e.preventDefault();
                        if (crumb.onClick) crumb.onClick();
                        else if (crumb.href) router.push(crumb.href);
                      }}
                      className="cursor-pointer"
                    >
                      {crumb.label}
                    </BreadcrumbLink>
                  ) : (
                    <BreadcrumbPage className="min-w-0">
                      <Truncate>{crumb.label}</Truncate>
                    </BreadcrumbPage>
                  )}
                </BreadcrumbItem>
                {!isLast && <BreadcrumbSeparator className="shrink-0" />}
              </Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex-1" />

      {headerActions && (
        <div className="flex shrink-0 items-center gap-2">{headerActions}</div>
      )}
    </header>
  );
}
